import mixpanel from "mixpanel-browser";
import { Controller, useFieldArray, useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { styled } from "styled-components";
import { routeList } from "../../App";
import { lang } from "../../assets/languages/en.language";
import { QuestionKeyList } from "../../configs/config";
import { theme } from "../../configs/theme";
import { usePwaViewportWidth } from "../../hooks/useScreenWidth";
import api from "../../services/api";
import { getVpFontSize } from "../../services/utils";
import { useUserStore } from "../../store/useUserStore";
import { PurpleButton, SwitchButton } from "../../styles/Button.style";
import { MainContainer, RootContainer } from "../../styles/Container.style";
import { StyledForm } from "../../styles/Form";
import { BigTitle, Paragraph, Title } from "../../styles/Text";
import { Column, Gap, Row } from "../../styles/Utils";

interface Question {
  key: QuestionKeyList;
  answer: string;
  enabled: boolean;
}

interface IFormData {
  questions: Question[];
}

export default function UpdateQuestionsPage() {
  const navigate = useNavigate();
  const viewportWidth = usePwaViewportWidth();
  const {
    profile: { questions },
    setQuestions,
  } = useUserStore();

  const defaultQuestions = Object.values(QuestionKeyList).map((key) => {
    const question = questions.find((e) => e.key == key);
    if (question) {
      const { answer, enabled } = question;
      return { key, answer: answer, enabled: enabled };
    } else return { key, answer: "", enabled: false };
  });

  const {
    control,
    handleSubmit,
    formState: { isValid },
    watch,
  } = useForm<IFormData>({
    defaultValues: {
      questions: defaultQuestions,
    },
  });

  const { fields } = useFieldArray({
    control,
    name: "questions",
  });

  const onSubmit = async (data: IFormData) => {
    console.log(data);
    try {
      await api.updateQuestions(data);
      mixpanel.track("Update Questions");
      setQuestions(data.questions);
      navigate(routeList.profile);
    } catch (err) {
      console.log("Error updating questions", err);
      alert("Error updating questions");
    }
  };

  return (
    <RootContainer>
      <BigTitle $fontSize={getVpFontSize(viewportWidth, 12)}>{lang.updateAboutThemPage.title}</BigTitle>
      <Gap $height={1} />
      <MainContainer>
        <StyledForm>
          <Paragraph>{lang.updateAboutThemPage.message}</Paragraph>
          <Gap $height={1} />
          <Column $gap={1}>
            {fields.map((field, index) => {
              const langQuestion = lang.updateAboutThemPage.questions.find((e) => e.key == field.key);
              const enabled = watch(`questions.${index}.enabled`);
              return (
                <QuestionContainer key={field.id} $disabled={!enabled}>
                  <Column>
                    <Row $justifyContent="space-between">
                      <Title $color={theme.color.tertiaryLight}>{langQuestion?.title}</Title>
                      <Controller
                        name={`questions.${index}.enabled` as const}
                        control={control}
                        render={({ field }) => (
                          <SwitchButton size={1} checked={field.value} onClick={() => field.onChange(!field.value)} />
                        )}
                      />
                    </Row>
                    <Gap $height={0.5} />
                    <Controller
                      name={`questions.${index}.answer` as const}
                      control={control}
                      render={({ field }) => (
                        <TextInput {...field} placeholder={langQuestion?.placeholder} rows={4} maxLength={300} />
                      )}
                    />
                  </Column>
                </QuestionContainer>
              );
            })}
          </Column>
        </StyledForm>
      </MainContainer>
      <Gap $height={1.5} />
      <PurpleButton type="submit" disabled={!isValid} onClick={handleSubmit(onSubmit)}>
        <span>{lang.buttons.continue}</span>
      </PurpleButton>
    </RootContainer>
  );
}

const TextInput = styled.textarea`
  color: ${(props) => props.theme.color.tertiaryLight};
  background: transparent;
  border: none;
  font-size: ${(props) => props.theme.font.size.title.medium};
  &::placeholder {
    /* color: gray; */
    opacity: 0.7;
    font-style: italic;
  }
`;

const QuestionContainer = styled(Column)<{ $disabled: boolean }>`
  padding: 2rem;
  border-radius: 30px;
  background-color: ${(props) => props.theme.color.primaryDark};
  width: 100%;
  opacity: ${(props) => props.$disabled && 0.5};
`;
